// Contenu SEO riche et unique par page pilier (cocon sémantique ImmoProBoost).
// Chaque entrée : h1, lead, sections (H2 + paragraphes/listes/tableau), FAQ (FAQPage schema),
// related (maillage interne), cta, schemaType (SoftwareApplication pour les pages logiciel).
// Objectif : contenu long, unique et utile pour ranker sur les requêtes concurrentielles.

const APP = "ImmoProBoost";
const PRICE = "99 €/mois";

// Bloc réutilisable : liste des 21 modules (résumé) pour enrichir les pages logiciel.
const MODULES_SHORT = [
  "Prospects & CRM vendeurs", "Mandat IA (OCR)", "Analyse de marché DVF", "Gestion des biens",
  "Annonces IA multi-variantes", "Communication & réseaux", "Home staging IA", "Création vidéo",
  "Calendrier & suivi de mandat", "Boîte à outils (35 calculateurs)", "CRM & matching acquéreurs",
  "Suggestions & alertes", "Assistant IA immobilier", "Documents immobiliers (30+)", "Retouche photo IA",
  "Prospection sectorisée", "Cadastre & parcelle", "Rapport d'expertise Pro", "Studio Flyers & affiches",
  "Google My Business", "Chatbot Pro",
];

const CTA_PARTNER = { label: "Découvrir les 21 modules", to: "/devenir-agent-partenaire" };
const CTA_ESTIM = { label: "Estimer un bien gratuitement", to: "/estimation-immobiliere-gratuite" };

export const SEO_CONTENT = {
  // ================= PILIER PRINCIPAL : LOGICIEL IMMOBILIER =================
  "/logiciel-immobilier": {
    schemaType: "SoftwareApplication",
    badge: "Logiciel immobilier tout-en-un",
    h1: "Logiciel immobilier tout-en-un pour agents et mandataires",
    lead: `${APP} est un logiciel immobilier français, en ligne (SaaS/cloud), qui réunit dans une seule plateforme tout ce dont un agent ou un mandataire a besoin pour prospecter, estimer, publier et vendre : CRM, estimation DVF, annonces IA, mandats, documents, home staging, vidéo et prospection sectorisée.`,
    intro: [
      `Choisir un logiciel immobilier ne devrait pas obliger à jongler entre dix outils. La plupart des professionnels utilisent un CRM d'un côté, un générateur d'annonces de l'autre, un tableur pour les estimations, un logiciel de retouche photo, et perdent un temps précieux à copier-coller. ${APP} rassemble ces 21 fonctions dans un seul abonnement à ${PRICE}, sans engagement.`,
      `Conçu et hébergé en France, respectueux du RGPD et des obligations d'annonce (DPE, mentions légales, honoraires, loi Hoguet), ${APP} s'adresse autant à l'agent en agence qu'au mandataire indépendant en réseau. L'objectif est simple : gagner des mandats, publier plus vite et vendre au meilleur prix.`,
    ],
    sections: [
      {
        h2: "Qu'est-ce qu'un logiciel immobilier tout-en-un ?",
        p: [
          `Un logiciel immobilier tout-en-un centralise la relation client (CRM), la gestion des biens, la production d'annonces et de documents, l'estimation et la prospection. Au lieu de multiplier les abonnements, l'agent pilote toute son activité depuis un tableau de bord unique, accessible sur ordinateur, tablette et mobile.`,
          `${APP} va plus loin en intégrant l'intelligence artificielle à chaque étape : rédaction d'annonces, home staging virtuel, retouche photo, assistant expert, chatbot et génération de courriers de prospection.`,
        ],
      },
      {
        h2: "Les 21 modules inclus",
        p: [`Tout est compris dans l'abonnement unique, sans option payante cachée :`],
        bullets: MODULES_SHORT,
      },
      {
        h2: "Pourquoi choisir un logiciel immobilier français et en ligne (SaaS) ?",
        p: [
          `Un logiciel en ligne (cloud/SaaS) ne s'installe pas : les mises à jour, la sécurité et les sauvegardes sont automatiques. Vous accédez à vos dossiers partout, y compris en rendez-vous vendeur depuis votre mobile.`,
          `Être hébergé en France et conforme RGPD garantit que les données de vos vendeurs et acquéreurs sont traitées dans un cadre légal clair — un argument de confiance décisif face aux propriétaires.`,
        ],
      },
      {
        h2: "Comparatif : outils séparés vs logiciel tout-en-un",
        table: {
          caption: "Ce que change un logiciel immobilier unifié au quotidien",
          headers: ["Besoin", "Outils séparés", `Avec ${APP}`],
          rows: [
            ["CRM vendeurs & acquéreurs", "Abonnement dédié", "Inclus"],
            ["Estimation basée sur les ventes DVF", "Tableur manuel", "Inclus, automatisé"],
            ["Annonces optimisées portails & réseaux", "Rédaction manuelle", "Généré par IA en 1 clic"],
            ["Home staging & retouche photo", "Logiciel + prestataire", "Inclus (IA)"],
            ["Documents (mandat, bon de visite…)", "Modèles Word épars", "30+ modèles conformes"],
            ["Coût mensuel", "150 à 400 €", `${PRICE}`],
          ],
        },
      },
      {
        h2: "Combien coûte un logiciel immobilier ?",
        p: [
          `Les logiciels immobiliers du marché vont de 50 € à plus de 400 €/mois selon les modules. ${APP} propose l'ensemble de ses 21 modules pour ${PRICE}, sans engagement, avec un premier mois à tarif réduit. Vous remplacez plusieurs abonnements par un seul.`,
        ],
      },
    ],
    faq: [
      { q: "Qu'est-ce qu'un logiciel immobilier ?", a: "C'est une plateforme qui centralise la gestion des contacts (CRM), des biens, des annonces, des estimations et des documents d'un professionnel de l'immobilier. ImmoProBoost y ajoute 21 modules dopés à l'IA." },
      { q: "Le logiciel est-il adapté aux agents comme aux mandataires ?", a: "Oui. ImmoProBoost convient aux agents en agence et aux mandataires indépendants en réseau, avec une page publique par bien et une identité professionnelle personnalisable (SIRET, RSAC, carte pro)." },
      { q: "Faut-il installer quelque chose ?", a: "Non, c'est un logiciel 100 % en ligne (SaaS). Vous vous connectez depuis un navigateur sur ordinateur, tablette ou mobile, sans installation ni maintenance." },
      { q: "Les données sont-elles conformes au RGPD ?", a: "Oui. ImmoProBoost est hébergé en France, respecte le RGPD et intègre les obligations d'annonce françaises (DPE, mentions légales, honoraires)." },
      { q: "Combien coûte ImmoProBoost ?", a: `${PRICE} sans engagement, avec les 21 modules inclus et un premier mois à tarif réduit.` },
    ],
    related: [
      { label: "CRM immobilier", to: "/crm-immobilier" },
      { label: "Logiciel pour mandataire immobilier", to: "/logiciel-mandataire-immobilier" },
      { label: "Logiciel de prospection immobilière", to: "/logiciel-de-prospection-immobiliere" },
      { label: "Logiciel d'estimation immobilière", to: "/logiciel-estimation-immobiliere" },
      { label: "Générateur d'annonce immobilière IA", to: "/generateur-annonce-immobiliere" },
      { label: "Toutes les fonctionnalités", to: "/fonctionnalites" },
    ],
    cta: CTA_PARTNER,
  },

  // ================= MEILLEUR LOGICIEL IMMOBILIER =================
  "/meilleur-logiciel-immobilier": {
    schemaType: "SoftwareApplication",
    badge: "Comparatif",
    h1: "Quel est le meilleur logiciel immobilier en 2026 ?",
    lead: `Le meilleur logiciel immobilier est celui qui remplace tous vos outils, vous fait gagner des mandats et reste simple à utiliser. Voici les critères qui comptent vraiment et pourquoi ${APP} coche toutes les cases pour les agents et mandataires indépendants.`,
    intro: [
      `« Meilleur logiciel immobilier » ne veut pas dire « le plus cher » ni « le plus complexe ». Cela veut dire : le meilleur rapport entre le nombre de tâches réellement automatisées, la conformité française et le prix. Un bon logiciel doit vous faire gagner du temps dès la première semaine.`,
      `Nous avons construit ${APP} autour de ce principe : 21 modules, une prise en main immédiate, et un tarif unique de ${PRICE} qui remplace 4 à 6 abonnements.`,
    ],
    sections: [
      {
        h2: "Les 7 critères d'un bon logiciel immobilier",
        bullets: [
          "Couverture fonctionnelle : CRM, biens, annonces, estimation, documents, prospection réunis",
          "Intelligence artificielle utile (annonces, photos, assistant) et non gadget",
          "Conformité française : DPE, mentions légales, honoraires, RGPD, loi Hoguet",
          "Mobilité : accès web sur mobile en rendez-vous vendeur",
          "Simplicité : prise en main sans formation lourde",
          "Prix transparent, sans engagement ni options cachées",
          "Génération de mandats : outils de prospection et de captation de vendeurs",
        ],
      },
      {
        h2: `Pourquoi ${APP} est un excellent choix`,
        p: [
          `${APP} réunit les 21 modules essentiels dans un seul abonnement, avec l'IA intégrée à chaque étape (annonces, home staging, retouche photo, assistant, chatbot). L'estimation s'appuie sur les ventes réelles DVF et la prospection livre chaque jour des prospects sectorisés.`,
        ],
        bullets: MODULES_SHORT,
      },
      {
        h2: "Tableau de décision rapide",
        table: {
          caption: "Choisir selon votre profil",
          headers: ["Profil", "Priorité", "Recommandation"],
          rows: [
            ["Mandataire indépendant", "Prospecter & capter des mandats", `${APP} (prospection + CRM + annonces)`],
            ["Agent en agence", "Gérer biens & vendeurs", `${APP} (CRM + biens + documents)`],
            ["Débutant", "Simplicité & tout-en-un", `${APP} (un seul outil)`],
            ["Confirmé", "IA & productivité", `${APP} (annonces IA, home staging, vidéo)`],
          ],
        },
      },
    ],
    faq: [
      { q: "Quel est le meilleur logiciel immobilier pour un mandataire ?", a: "Pour un mandataire indépendant, le meilleur logiciel combine prospection sectorisée, CRM, annonces IA et documents conformes. ImmoProBoost réunit ces fonctions dans un seul abonnement à 99 €/mois." },
      { q: "Existe-t-il un logiciel immobilier gratuit ?", a: "Les versions gratuites sont très limitées. ImmoProBoost propose un premier mois à tarif réduit pour tester l'ensemble des 21 modules sans engagement." },
      { q: "Quel logiciel immobilier pour débuter ?", a: "Un outil tout-en-un et simple évite d'accumuler les abonnements. ImmoProBoost est pensé pour une prise en main immédiate, sans formation lourde." },
    ],
    related: [
      { label: "Logiciel immobilier tout-en-un", to: "/logiciel-immobilier" },
      { label: "Logiciel pour mandataire immobilier", to: "/logiciel-mandataire-immobilier" },
      { label: "CRM immobilier", to: "/crm-immobilier" },
      { label: "Toutes les fonctionnalités", to: "/fonctionnalites" },
    ],
    cta: CTA_PARTNER,
  },

  // ================= CRM IMMOBILIER =================
  "/crm-immobilier": {
    schemaType: "SoftwareApplication",
    badge: "CRM immobilier",
    h1: "CRM immobilier en ligne pour agents et mandataires",
    lead: `Un CRM immobilier centralise vos vendeurs, acquéreurs, tâches et relances pour ne plus jamais perdre un contact. Le CRM ${APP} ajoute un score de chaleur automatique, la détection des doublons et un pipeline visuel du premier appel jusqu'au mandat signé.`,
    intro: [
      `Dans l'immobilier, la différence entre un mandat gagné et un mandat perdu se joue souvent sur la relance. Un CRM immobilier bien utilisé transforme des contacts éparpillés en un pipeline clair où chaque vendeur est suivi au bon moment.`,
      `Le CRM de ${APP} est pensé pour les professionnels de terrain : rapide, mobile et connecté aux autres modules (estimation, annonces, documents), pour agir sans changer d'outil.`,
    ],
    sections: [
      {
        h2: "À quoi sert un CRM immobilier ?",
        p: [
          `Un CRM (Customer Relationship Management) immobilier vous permet de suivre chaque prospect vendeur et acquéreur, d'historiser les échanges, de planifier les relances et de mesurer votre taux de transformation. C'est le cœur commercial de votre activité.`,
        ],
        bullets: [
          "Pipeline Kanban glisser-déposer (Nouveau → RDV → Mandat → Vendu)",
          "Score de chaleur automatique du prospect (0 à 100)",
          "Détection automatique des doublons",
          "Timeline complète des interactions par contact",
          "Relance automatique selon l'étape du pipeline",
          "Contact Email / Appel / WhatsApp en 1 clic",
          "Import et export CSV",
        ],
      },
      {
        h2: "CRM vendeurs et CRM acquéreurs (matching)",
        p: [
          `${APP} distingue le suivi vendeurs (captation et suivi de mandat) du CRM acquéreurs avec matching : chaque nouveau bien est automatiquement rapproché des acheteurs dont les critères correspondent. Vous proposez le bon bien au bon acheteur, immédiatement.`,
        ],
      },
      {
        h2: "CRM immobilier en ligne, mobile et connecté",
        p: [
          `Parce qu'il est en ligne, le CRM ${APP} est accessible en rendez-vous depuis votre téléphone. Créez une fiche vendeur, lancez une estimation DVF et envoyez un compte-rendu de visite sans quitter la plateforme.`,
        ],
      },
    ],
    faq: [
      { q: "Qu'est-ce qu'un CRM immobilier ?", a: "Un CRM immobilier est un logiciel qui centralise vos contacts vendeurs et acquéreurs, l'historique des échanges, les tâches et les relances, pour piloter votre activité commerciale et améliorer votre taux de transformation." },
      { q: "Le CRM ImmoProBoost convient-il aux mandataires ?", a: "Oui, il est adapté aux mandataires indépendants comme aux agents en agence, avec un pipeline visuel, le score de chaleur et le matching acquéreurs." },
      { q: "Le CRM est-il inclus dans l'abonnement ?", a: "Oui, le CRM vendeurs et le CRM acquéreurs avec matching font partie des 21 modules inclus à 99 €/mois, sans supplément." },
      { q: "Peut-on importer ses contacts existants ?", a: "Oui, l'import et l'export CSV permettent de récupérer vos contacts et de les intégrer au pipeline en quelques minutes." },
    ],
    related: [
      { label: "Logiciel immobilier tout-en-un", to: "/logiciel-immobilier" },
      { label: "CRM pour agent immobilier", to: "/logiciel-agent-immobilier" },
      { label: "Logiciel de prospection immobilière", to: "/logiciel-de-prospection-immobiliere" },
      { label: "Logiciel pour mandataire", to: "/logiciel-mandataire-immobilier" },
      { label: "Toutes les fonctionnalités", to: "/fonctionnalites" },
    ],
    cta: CTA_PARTNER,
  },

  // ================= LOGICIEL MANDATAIRE IMMOBILIER =================
  "/logiciel-mandataire-immobilier": {
    schemaType: "SoftwareApplication",
    badge: "Pour mandataires & réseaux",
    h1: "Logiciel pour mandataire immobilier indépendant",
    lead: `Mandataire ou négociateur immobilier en réseau ? ${APP} est le logiciel tout-en-un qui vous rend autonome : prospection sectorisée, CRM, estimation DVF, annonces IA, mandats et une page publique professionnelle par bien — le tout à ${PRICE}.`,
    intro: [
      `Le mandataire indépendant n'a pas de vitrine ni de secrétariat : son logiciel EST son agence. Il doit prospecter seul, capter des mandats, publier des annonces impeccables et suivre ses vendeurs sans équipe support.`,
      `${APP} a été conçu pour cette réalité : autonomie totale, image professionnelle et productivité maximale, que vous soyez chez SAFTI, iad, Capifrance, Optimhome, Dr House ou indépendant.`,
    ],
    sections: [
      {
        h2: "Les besoins spécifiques d'un mandataire",
        bullets: [
          "Prospecter un secteur précis et capter des mandats sans agence physique",
          "Projeter une image professionnelle (annonces, photos, page bien)",
          "Gérer seul le suivi vendeur et acquéreur",
          "Produire des documents conformes (mandat, bon de visite, offre)",
          "Maîtriser ses coûts avec un seul abonnement",
        ],
      },
      {
        h2: `Ce que ${APP} apporte au négociateur indépendant`,
        p: [
          `La prospection sectorisée livre chaque jour jusqu'à 25 prospects géolocalisés (DPE récents ADEME, passoires thermiques F/G, ventes DVF, SCI locales, pige particuliers). Le CRM les organise, l'estimation DVF prépare le rendez-vous vendeur, et les annonces IA publient un bien conforme en quelques minutes.`,
        ],
        bullets: MODULES_SHORT,
      },
      {
        h2: "Une page publique professionnelle par bien",
        p: [
          `Chaque bien dispose d'une page web indexable avec DPE, mentions légales et coordonnées. Vous partagez un lien propre et professionnel, gérez vos flyers, votre fiche Google My Business et un chatbot pour ne manquer aucun contact.`,
        ],
      },
    ],
    faq: [
      { q: "Quel logiciel pour un mandataire immobilier indépendant ?", a: "Un mandataire a besoin d'un outil autonome couvrant prospection, CRM, estimation, annonces et documents. ImmoProBoost réunit ces fonctions dans un seul abonnement à 99 €/mois." },
      { q: "ImmoProBoost fonctionne-t-il avec les réseaux (SAFTI, iad, Capifrance…) ?", a: "Oui, ImmoProBoost est un logiciel indépendant et complémentaire, utilisable quel que soit votre réseau, avec votre propre identité professionnelle (SIRET, RSAC, carte pro)." },
      { q: "Comment un mandataire capte-t-il plus de mandats ?", a: "Grâce à la prospection sectorisée quotidienne, aux courriers de prospection IA, au rapport d'expertise professionnel et à l'estimation DVF qui crédibilise le rendez-vous vendeur." },
    ],
    related: [
      { label: "Logiciel immobilier tout-en-un", to: "/logiciel-immobilier" },
      { label: "Logiciel négociateur immobilier", to: "/logiciel-negociateur-immobilier" },
      { label: "Logiciel de prospection immobilière", to: "/logiciel-de-prospection-immobiliere" },
      { label: "CRM immobilier", to: "/crm-immobilier" },
      { label: "Devenir agent partenaire", to: "/devenir-agent-partenaire" },
    ],
    cta: CTA_PARTNER,
  },

  // ================= LOGICIEL AGENT IMMOBILIER =================
  "/logiciel-agent-immobilier": {
    schemaType: "SoftwareApplication",
    badge: "Pour agents immobiliers",
    h1: "Logiciel pour agent immobilier",
    lead: `${APP} est le logiciel de l'agent immobilier moderne : un CRM puissant, l'estimation DVF, les annonces IA, plus de 30 documents conformes et 35 calculateurs, réunis dans une seule plateforme en ligne à ${PRICE}.`,
    intro: [
      `L'agent immobilier passe trop de temps sur des tâches à faible valeur : ressaisir des données, chercher un modèle de document, retoucher des photos, rédiger des annonces. Un bon logiciel agent immobilier automatise ces tâches pour libérer du temps commercial.`,
      `Avec ${APP}, l'agent gère ses vendeurs, ses acquéreurs, ses biens et ses documents au même endroit, avec l'IA en renfort à chaque étape.`,
    ],
    sections: [
      {
        h2: "Les fonctions clés pour un agent",
        bullets: [
          "CRM vendeurs & acquéreurs avec pipeline et relances",
          "Estimation appuyée sur les ventes réelles DVF",
          "Annonces IA conformes (DPE, mentions, honoraires)",
          "30+ documents (mandat, bon de visite, offre, compte-rendu)",
          "Home staging et retouche photo par IA",
          "35 calculateurs (net vendeur, frais de notaire, capacité d'emprunt…)",
        ],
      },
      {
        h2: "Gagner du temps et paraître plus professionnel",
        p: [
          `Chaque bien obtient une page publique soignée, des photos améliorées et une annonce optimisée. L'agent envoie des documents conformes en PDF, suit ses statistiques et automatise ses relances — une image premium qui rassure vendeurs et acquéreurs.`,
        ],
      },
    ],
    faq: [
      { q: "Quel logiciel pour un agent immobilier ?", a: "Un logiciel agent immobilier doit couvrir CRM, biens, estimation, annonces et documents. ImmoProBoost réunit 21 modules dopés à l'IA dans un seul abonnement à 99 €/mois." },
      { q: "Le logiciel gère-t-il les obligations légales des annonces ?", a: "Oui, ImmoProBoost injecte automatiquement le DPE, les mentions légales et les honoraires, conformément à la réglementation française." },
      { q: "Peut-on l'utiliser sur mobile en rendez-vous ?", a: "Oui, c'est un logiciel en ligne accessible sur mobile, tablette et ordinateur." },
    ],
    related: [
      { label: "Logiciel immobilier tout-en-un", to: "/logiciel-immobilier" },
      { label: "CRM immobilier", to: "/crm-immobilier" },
      { label: "Agent immobilier IA", to: "/agent-immobilier-ia" },
      { label: "Documents immobiliers", to: "/documents-immobiliers" },
      { label: "Toutes les fonctionnalités", to: "/fonctionnalites" },
    ],
    cta: CTA_PARTNER,
  },

  // ================= LOGICIEL DE PROSPECTION IMMOBILIERE =================
  "/logiciel-de-prospection-immobiliere": {
    schemaType: "SoftwareApplication",
    badge: "Prospection & captation",
    h1: "Logiciel de prospection immobilière",
    lead: `La prospection est le nerf de la guerre. Le logiciel de prospection immobilière ${APP} livre chaque jour jusqu'à 25 prospects géolocalisés dans votre secteur et génère automatiquement vos courriers, votre pige et votre itinéraire de boîtage.`,
    intro: [
      `Trouver des mandats demande de la régularité et de la méthode. Un logiciel de prospection immobilière transforme cette régularité en système : il identifie les vendeurs potentiels, priorise les pistes et automatise la prise de contact.`,
      `${APP} croise des données publiques réelles (DPE ADEME, ventes DVF, SCI locales, pige particuliers) pour vous proposer, chaque matin, un lot de prospects sectorisés prêts à travailler.`,
    ],
    sections: [
      {
        h2: "Comment fonctionne la prospection sectorisée",
        bullets: [
          "Jusqu'à 25 prospects géolocalisés livrés chaque jour",
          "DPE récents ADEME (vendeurs potentiels) & passoires thermiques F/G",
          "Marchands de biens & SCI locales (données publiques réelles)",
          "Ventes récentes du secteur (DVF) + pige particuliers",
          "Filtres avancés : rayon, communes limitrophes, type, surface, fraîcheur DPE",
          "Score de priorité, statuts, notes & anti-doublon",
          "Carte de chaleur des zones à fort potentiel",
          "Courriers de prospection IA + itinéraire de boîtage + export CSV",
          "Relance automatique J+3 des prospects non convertis",
        ],
      },
      {
        h2: "De la pige au mandat, sans rupture",
        p: [
          `Un prospect détecté devient une fiche CRM, puis un rendez-vous préparé par une estimation DVF, puis un mandat signé avec les documents conformes. Toute la chaîne est intégrée : vous ne perdez plus aucune piste entre deux outils.`,
        ],
      },
    ],
    faq: [
      { q: "Qu'est-ce qu'un logiciel de prospection immobilière ?", a: "C'est un outil qui identifie les vendeurs potentiels d'un secteur et automatise la prise de contact (courriers, pige, boîtage). ImmoProBoost livre chaque jour des prospects géolocalisés issus de données publiques réelles." },
      { q: "D'où viennent les prospects ?", a: "De sources publiques : DPE ADEME, ventes DVF, SCI locales et pige particuliers, recoupées et priorisées pour votre secteur." },
      { q: "La prospection est-elle conforme au RGPD ?", a: "Oui, elle s'appuie sur des données publiques et un cadre respectueux du RGPD, avec des courriers de prospection professionnels." },
    ],
    related: [
      { label: "Logiciel de pige immobilière", to: "/logiciel-pige-immobiliere" },
      { label: "Prospection immobilière : méthodes", to: "/prospection-immobiliere" },
      { label: "Logiciel pour mandataire", to: "/logiciel-mandataire-immobilier" },
      { label: "CRM immobilier", to: "/crm-immobilier" },
      { label: "Logiciel immobilier tout-en-un", to: "/logiciel-immobilier" },
    ],
    cta: CTA_PARTNER,
  },

  // ================= LOGICIEL PIGE IMMOBILIERE =================
  "/logiciel-pige-immobiliere": {
    schemaType: "SoftwareApplication",
    badge: "Pige immobilière",
    h1: "Logiciel de pige immobilière",
    lead: `La pige immobilière consiste à repérer les biens vendus par des particuliers pour proposer vos services. Le logiciel de pige ${APP} centralise la pige particuliers (Leboncoin, PAP) et les ventes récentes, puis génère vos scripts d'appel et vos courriers.`,
    intro: [
      `Faire de la pige à la main est chronophage : consulter les portails, noter les annonces de particuliers, retrouver les coordonnées, relancer. Un logiciel de pige immobilière industrialise cette routine et vous fait gagner des heures chaque semaine.`,
      `${APP} intègre la pige à la prospection sectorisée : les biens de particuliers de votre secteur remontent automatiquement dans votre liste de pistes, avec un score de priorité.`,
    ],
    sections: [
      {
        h2: "Ce que fait le module pige",
        bullets: [
          "Pige particuliers (Leboncoin, PAP) sur votre secteur",
          "Ventes récentes DVF pour contextualiser vos appels",
          "Scripts d'appel vendeur générés par IA",
          "Suivi des pistes dans le CRM, anti-doublon",
          "Relance automatique des pistes non converties",
        ],
      },
      {
        h2: "Transformer une pige en mandat",
        p: [
          `Une piste de pige devient une fiche vendeur, préparée par une estimation DVF crédible et un rapport d'expertise professionnel. Vous arrivez au rendez-vous avec des arguments chiffrés, pas de simples promesses.`,
        ],
      },
    ],
    faq: [
      { q: "Qu'est-ce que la pige immobilière ?", a: "La pige immobilière consiste à repérer les biens mis en vente par des particuliers pour leur proposer un accompagnement professionnel. Un logiciel de pige automatise cette veille." },
      { q: "ImmoProBoost fait-il la pige automatiquement ?", a: "Oui, la pige particuliers et les ventes récentes de votre secteur remontent dans votre liste de prospects, avec scripts d'appel et relances." },
    ],
    related: [
      { label: "Logiciel de prospection immobilière", to: "/logiciel-de-prospection-immobiliere" },
      { label: "Prospection immobilière", to: "/prospection-immobiliere" },
      { label: "CRM immobilier", to: "/crm-immobilier" },
      { label: "Logiciel immobilier tout-en-un", to: "/logiciel-immobilier" },
    ],
    cta: CTA_PARTNER,
  },

  // ================= LOGICIEL ESTIMATION IMMOBILIERE =================
  "/logiciel-estimation-immobiliere": {
    schemaType: "SoftwareApplication",
    badge: "Estimation DVF",
    h1: "Logiciel d'estimation immobilière",
    lead: `Le logiciel d'estimation immobilière ${APP} calcule une fourchette de prix appuyée sur les ventes réelles DVF, ajustée par l'état, le DPE, l'étage et les travaux, et produit un rapport d'expertise professionnel prêt à présenter au vendeur.`,
    intro: [
      `Une estimation crédible est la clé pour signer un mandat au bon prix. Les estimateurs automatiques grand public manquent de finesse : ils ignorent l'état, la vue, l'étage ou les travaux. Un vrai logiciel d'estimation immobilière corrige ces angles morts.`,
      `${APP} part des ventes réelles publiées par l'État (DVF) puis applique des coefficients métier pour aboutir à une fourchette défendable, avec comparables à l'appui.`,
    ],
    sections: [
      {
        h2: "Une méthode transparente et défendable",
        bullets: [
          "Base : ventes réelles DVF du secteur",
          "Ajustements : état, DPE/GES, étage, vue, travaux, standing",
          "Fourchette basse / prix cible / fourchette haute",
          "Comparables affichés et argumentaire prix",
          "Rapport d'expertise Pro exportable en PDF",
        ],
      },
      {
        h2: "Du calcul au mandat",
        p: [
          `L'estimation alimente directement la fiche vendeur du CRM et le rapport d'expertise. Vous présentez un document professionnel, argumentez le prix avec des données objectives et sécurisez la signature du mandat.`,
        ],
      },
    ],
    faq: [
      { q: "Comment est calculée l'estimation ?", a: "À partir des ventes réelles DVF du secteur, ajustées par l'état du bien, le DPE, l'étage, la vue et les travaux, pour obtenir une fourchette réaliste et argumentée." },
      { q: "L'estimation remplace-t-elle une expertise ?", a: "Elle constitue une aide à la décision solide. Une visite professionnelle reste nécessaire pour fixer le prix de mise en vente définitif." },
      { q: "Peut-on éditer un rapport pour le vendeur ?", a: "Oui, ImmoProBoost génère un rapport d'expertise Pro exportable en PDF, avec comparables et argumentaire." },
    ],
    related: [
      { label: "Estimation immobilière gratuite", to: "/estimation-immobiliere-gratuite" },
      { label: "Prix au m²", to: "/prix-au-m2" },
      { label: "Estimation d'appartement", to: "/estimation-appartement" },
      { label: "Estimation de maison", to: "/estimation-maison" },
      { label: "Logiciel immobilier tout-en-un", to: "/logiciel-immobilier" },
    ],
    cta: CTA_ESTIM,
  },

  // ================= LOGICIEL GESTION MANDATS =================
  "/logiciel-gestion-mandats": {
    schemaType: "SoftwareApplication",
    badge: "Mandats & suivi",
    h1: "Logiciel de gestion des mandats immobiliers",
    lead: `Numérisez, suivez et pilotez vos mandats avec ${APP} : reconnaissance OCR du mandat, création de fiche bien automatique, plan de commercialisation J+7 à J+60 et compte-rendus vendeur en un clic.`,
    intro: [
      `La gestion des mandats ne s'arrête pas à la signature. Un bon logiciel de gestion des mandats suit chaque étape de la commercialisation, relance au bon moment et tient le vendeur informé — ce qui renouvelle la confiance et facilite les ajustements de prix.`,
      `${APP} transforme un mandat papier en dossier numérique exploitable en quelques secondes grâce à l'OCR.`,
    ],
    sections: [
      {
        h2: "De la numérisation au suivi",
        bullets: [
          "Mandat IA (OCR) : numérisation d'un mandat en quelques secondes",
          "Création automatique de la fiche bien",
          "Plan de commercialisation : jalons J+7, J+15, J+30, J+45, J+60",
          "Compte-rendus de suivi vendeur (statistiques, retours, actions)",
          "Documents conformes : mandat, avenant, autorisation de diffusion",
        ],
      },
      {
        h2: "Garder le vendeur engagé",
        p: [
          `Le calendrier intelligent déclenche les bonnes actions au bon moment et propose un lien public de prise de rendez-vous. Vous démontrez votre activité, justifiez vos recommandations et sécurisez la relation jusqu'à la vente.`,
        ],
      },
    ],
    faq: [
      { q: "Comment numériser un mandat ?", a: "Le module Mandat IA (OCR) reconnaît le contenu d'un mandat photographié ou importé et crée automatiquement la fiche bien correspondante." },
      { q: "Peut-on suivre l'avancement d'un mandat ?", a: "Oui, un plan de commercialisation J+7 à J+60 génère les jalons et rappels, avec des compte-rendus vendeur prêts à envoyer." },
    ],
    related: [
      { label: "Logiciel immobilier tout-en-un", to: "/logiciel-immobilier" },
      { label: "Documents immobiliers", to: "/documents-immobiliers" },
      { label: "CRM immobilier", to: "/crm-immobilier" },
      { label: "Toutes les fonctionnalités", to: "/fonctionnalites" },
    ],
    cta: CTA_PARTNER,
  },

  // ================= LOGICIEL NEGOCIATEUR IMMOBILIER =================
  "/logiciel-negociateur-immobilier": {
    schemaType: "SoftwareApplication",
    badge: "Négociateur immobilier",
    h1: "Logiciel pour négociateur immobilier",
    lead: `Négociateur immobilier salarié ou indépendant, ${APP} vous donne les mêmes armes qu'une grande agence : CRM, estimation DVF, annonces IA, documents conformes et prospection — dans un seul logiciel à ${PRICE}.`,
    intro: [
      `Le négociateur immobilier vit de sa capacité à transformer des contacts en mandats puis en ventes. Son logiciel doit accélérer chaque étape : qualifier vite, argumenter juste, publier proprement et relancer sans oublier personne.`,
      `${APP} centralise tout cela et ajoute l'IA (annonces, photos, assistant) pour que le négociateur se concentre sur l'humain et la négociation.`,
    ],
    sections: [
      {
        h2: "Les outils du négociateur performant",
        bullets: [
          "Qualification rapide des vendeurs et acquéreurs (CRM + score)",
          "Argumentaire prix appuyé sur les ventes DVF",
          "Annonces IA et supports marketing premium",
          "Documents de négociation (offre, contre-offre, argumentaire prix)",
          "Relances automatiques pour ne perdre aucune opportunité",
        ],
      },
    ],
    faq: [
      { q: "Quelle différence entre agent, mandataire et négociateur ?", a: "Le négociateur travaille pour le compte d'une agence ou d'un réseau ; le mandataire est indépendant. Dans les deux cas, ImmoProBoost fournit les mêmes outils complets à 99 €/mois." },
      { q: "Le logiciel aide-t-il à la négociation ?", a: "Oui, avec des documents dédiés (offre, contre-offre, argumentaire prix) et un argumentaire basé sur les données DVF." },
    ],
    related: [
      { label: "Logiciel pour mandataire", to: "/logiciel-mandataire-immobilier" },
      { label: "CRM immobilier", to: "/crm-immobilier" },
      { label: "Logiciel de prospection immobilière", to: "/logiciel-de-prospection-immobiliere" },
      { label: "Logiciel immobilier tout-en-un", to: "/logiciel-immobilier" },
    ],
    cta: CTA_PARTNER,
  },

  // ================= PROSPECTION IMMOBILIERE (guide) =================
  "/prospection-immobiliere": {
    schemaType: "Article",
    badge: "Guide prospection",
    h1: "Prospection immobilière : méthodes et outils pour capter des mandats",
    lead: `La prospection immobilière regroupe toutes les actions pour trouver des vendeurs : pige, boîtage, phoning, réseaux, recommandations. Voici les méthodes qui fonctionnent en 2026 et comment un outil de prospection immobilière les rend systématiques.`,
    intro: [
      `Sans prospection, pas de mandat ; sans mandat, pas de vente. Pourtant, beaucoup de professionnels prospectent de façon irrégulière, faute de méthode ou d'outil. La clé est la régularité : quelques actions ciblées chaque jour valent mieux qu'un effort ponctuel.`,
      `Ce guide résume les canaux de prospection les plus efficaces et montre comment ${APP} les automatise pour vous.`,
    ],
    sections: [
      {
        h2: "Les canaux de prospection qui marchent",
        bullets: [
          "La pige particuliers (Leboncoin, PAP) : contacter les vendeurs directs",
          "Le boîtage ciblé : distribuer des courriers sur les zones à potentiel",
          "Le phoning : appeler avec un script vendeur préparé",
          "Les DPE récents et passoires thermiques : des vendeurs souvent motivés",
          "La recommandation et le réseau local (Google My Business, réseaux sociaux)",
        ],
      },
      {
        h2: "Rendre la prospection systématique avec un outil",
        p: [
          `Un outil de prospection immobilière comme ${APP} livre chaque jour des prospects géolocalisés, génère les courriers et les scripts, planifie l'itinéraire de boîtage et relance automatiquement les pistes non converties. La régularité devient un réflexe, pas une contrainte.`,
        ],
      },
      {
        h2: "Le tunnel : du premier contact au mandat",
        p: [
          `Chaque piste entre dans le CRM, reçoit un score, est relancée au bon moment, puis préparée par une estimation DVF avant le rendez-vous. Le taux de transformation grimpe car aucune étape n'est laissée au hasard.`,
        ],
      },
    ],
    faq: [
      { q: "Comment prospecter efficacement dans l'immobilier ?", a: "En combinant pige, boîtage ciblé, phoning et exploitation des DPE récents, avec une régularité quotidienne. Un outil de prospection automatise la détection des pistes et les relances." },
      { q: "Quel est le meilleur moment pour prospecter ?", a: "La régularité prime : mieux vaut quelques actions ciblées chaque jour qu'un effort ponctuel. L'automatisation des relances J+3 aide à rester constant." },
      { q: "Comment trouver des vendeurs motivés ?", a: "Les DPE récents, les passoires thermiques F/G et les ventes de particuliers repérées en pige révèlent souvent des vendeurs prêts à agir." },
    ],
    related: [
      { label: "Logiciel de prospection immobilière", to: "/logiciel-de-prospection-immobiliere" },
      { label: "Logiciel de pige immobilière", to: "/logiciel-pige-immobiliere" },
      { label: "Logiciel pour mandataire", to: "/logiciel-mandataire-immobilier" },
      { label: "CRM immobilier", to: "/crm-immobilier" },
    ],
    cta: CTA_PARTNER,
  },

  // ================= AGENT IMMOBILIER IA =================
  "/agent-immobilier-ia": {
    schemaType: "SoftwareApplication",
    badge: "Intelligence artificielle",
    h1: "Agent immobilier IA : votre copilote au quotidien",
    lead: `L'intelligence artificielle ne remplace pas l'agent : elle le rend plus rapide et plus professionnel. ${APP} intègre l'IA à chaque étape — annonces, photos, home staging, assistant expert et chatbot — pour vous faire gagner des heures chaque semaine.`,
    intro: [
      `« Agent immobilier IA » ne signifie pas déléguer son métier à une machine, mais s'équiper d'un copilote qui rédige, retouche, prépare et suggère. L'agent garde la main sur la relation et la négociation ; l'IA s'occupe des tâches répétitives.`,
      `${APP} met l'IA au service concret de votre activité, avec des garde-fous de conformité française.`,
    ],
    sections: [
      {
        h2: "Où l'IA vous fait gagner du temps",
        bullets: [
          "Annonces IA multi-variantes (portail, réseaux, courte, premium)",
          "Home staging virtuel : meubler ou moderniser une pièce",
          "Retouche photo IA : des photos dignes d'un shooting pro",
          "Assistant IA immobilier : arguments prix, e-mails, scripts",
          "Chatbot Pro : répondre 24h/24 aux visiteurs",
        ],
      },
      {
        h2: "Une IA encadrée et conforme",
        p: [
          `Les contenus générés injectent automatiquement les mentions légales et le DPE. L'IA reste une aide à la rédaction et à la décision : vous validez avant publication, en toute conformité avec la réglementation immobilière française.`,
        ],
      },
    ],
    faq: [
      { q: "L'IA va-t-elle remplacer les agents immobiliers ?", a: "Non. L'IA automatise les tâches répétitives (rédaction, retouche, préparation) mais la relation, la visite et la négociation restent humaines. ImmoProBoost est un copilote, pas un remplaçant." },
      { q: "Les annonces générées sont-elles conformes ?", a: "Oui, ImmoProBoost injecte automatiquement les mentions légales et le DPE dans les annonces générées." },
    ],
    related: [
      { label: "Logiciel immobilier IA", to: "/logiciel-immobilier-ia" },
      { label: "Générateur d'annonce immobilière IA", to: "/generateur-annonce-immobiliere" },
      { label: "Home staging virtuel", to: "/home-staging-virtuel" },
      { label: "Logiciel immobilier tout-en-un", to: "/logiciel-immobilier" },
    ],
    cta: CTA_PARTNER,
  },

  // ================= LOGICIEL IMMOBILIER IA =================
  "/logiciel-immobilier-ia": {
    schemaType: "SoftwareApplication",
    badge: "Logiciel immobilier IA",
    h1: "Logiciel immobilier avec intelligence artificielle",
    lead: `${APP} est un logiciel immobilier IA en ligne : il combine un CRM complet, l'estimation DVF et l'intelligence artificielle (annonces, photos, home staging, assistant, chatbot) pour automatiser votre production commerciale.`,
    intro: [
      `Un logiciel immobilier avec intelligence artificielle ne se contente pas de stocker des données : il produit du contenu, suggère des actions et fait gagner un temps considérable. C'est la nouvelle génération d'outils pour les professionnels de l'immobilier.`,
      `${APP} réunit 21 modules dont plusieurs propulsés par l'IA, dans un seul abonnement à ${PRICE}.`,
    ],
    sections: [
      {
        h2: "L'IA au cœur du logiciel",
        bullets: [
          "Rédaction d'annonces multi-variantes",
          "Home staging virtuel et retouche photo",
          "Assistant expert immobilier",
          "Courriers de prospection générés automatiquement",
          "Chatbot professionnel 24h/24",
        ],
      },
      {
        h2: "SaaS, cloud et conforme RGPD",
        p: [
          `${APP} est un logiciel immobilier SaaS hébergé en France : rien à installer, des mises à jour automatiques et une conformité RGPD native. Vous travaillez partout, en toute sécurité.`,
        ],
      },
    ],
    faq: [
      { q: "Qu'apporte l'IA dans un logiciel immobilier ?", a: "L'IA rédige les annonces, retouche les photos, réalise du home staging virtuel, assiste l'agent et anime un chatbot — automatisant la production de contenu et le suivi." },
      { q: "Le logiciel est-il en cloud ?", a: "Oui, ImmoProBoost est un logiciel immobilier SaaS/cloud, sans installation, accessible depuis un navigateur." },
    ],
    related: [
      { label: "Agent immobilier IA", to: "/agent-immobilier-ia" },
      { label: "Logiciel immobilier tout-en-un", to: "/logiciel-immobilier" },
      { label: "Générateur d'annonce immobilière IA", to: "/generateur-annonce-immobiliere" },
      { label: "CRM immobilier", to: "/crm-immobilier" },
    ],
    cta: CTA_PARTNER,
  },

  // ================= GENERATEUR ANNONCE IMMOBILIERE =================
  "/generateur-annonce-immobiliere": {
    schemaType: "SoftwareApplication",
    badge: "Annonces IA",
    h1: "Générateur d'annonce immobilière par IA",
    lead: `Rédigez des annonces qui vendent en quelques secondes. Le générateur d'annonce immobilière ${APP} produit 4 variantes optimisées (portail, réseaux, courte, premium) avec ton, longueur et langue réglables, et mentions légales injectées automatiquement.`,
    intro: [
      `Une annonce immobilière efficace doit accrocher, informer et convertir — tout en restant conforme. Écrire une bonne annonce prend du temps ; en écrire quatre versions pour différents canaux en prend encore plus.`,
      `Le générateur d'annonce IA de ${APP} rédige ces variantes en un clic, à partir de la fiche bien, et adapte le style au support.`,
    ],
    sections: [
      {
        h2: "4 variantes en un clic",
        bullets: [
          "Portail (SeLoger, Leboncoin) : complète et structurée",
          "Réseaux sociaux : accrocheuse, avec hashtags locaux",
          "Courte : idéale pour les alertes et le SMS",
          "Premium : storytelling haut de gamme",
        ],
      },
      {
        h2: "Conformité automatique",
        p: [
          `Le DPE, la classe climat, les honoraires et les mentions légales obligatoires sont injectés automatiquement. Vos annonces sont prêtes à diffuser sur tous les portails, sans risque de non-conformité.`,
        ],
      },
    ],
    faq: [
      { q: "Comment rédiger une bonne annonce immobilière ?", a: "Une bonne annonce accroche dès le titre, met en avant les points forts, reste factuelle et affiche les mentions obligatoires (DPE, honoraires). ImmoProBoost génère automatiquement 4 variantes optimisées et conformes." },
      { q: "Les annonces sont-elles conformes à la loi ?", a: "Oui, les mentions légales et le DPE sont injectés automatiquement dans chaque annonce générée." },
    ],
    related: [
      { label: "Logiciel immobilier IA", to: "/logiciel-immobilier-ia" },
      { label: "Home staging virtuel", to: "/home-staging-virtuel" },
      { label: "Agent immobilier IA", to: "/agent-immobilier-ia" },
      { label: "Logiciel immobilier tout-en-un", to: "/logiciel-immobilier" },
    ],
    cta: CTA_PARTNER,
  },

  // ================= DOCUMENTS IMMOBILIERS =================
  "/documents-immobiliers": {
    schemaType: "SoftwareApplication",
    badge: "Documents conformes",
    h1: "Documents immobiliers à remplir et exporter en PDF",
    lead: `${APP} met à disposition plus de 30 modèles de documents immobiliers conformes : mandat, bon de visite, offre d'achat, compte-rendu de visite, avis de valeur, argumentaire prix… À remplir, personnaliser et exporter en PDF en quelques minutes.`,
    intro: [
      `Chercher le bon modèle de document, le mettre à jour, l'adapter au dossier : autant de temps perdu. Une bibliothèque de documents immobiliers conformes, prête à remplir, sécurise vos transactions et renforce votre image professionnelle.`,
      `${APP} couvre toute la chaîne : découverte, estimation, mandat, commercialisation, négociation et suivi.`,
    ],
    sections: [
      {
        h2: "Les principaux documents inclus",
        bullets: [
          "Mandat de vente et avenants",
          "Bon de visite et compte-rendu de visite",
          "Offre d'achat et contre-offre",
          "Avis de valeur et fiche d'estimation",
          "Argumentaire prix et proposition de baisse de prix",
          "Checklists DPE et diagnostics obligatoires",
        ],
      },
      {
        h2: "Remplir, personnaliser, exporter",
        p: [
          `Chaque document se pré-remplit avec les données du bien et du contact, s'enrichit de blocs générés par IA si besoin, et s'exporte en PDF propre à votre image. Les mentions RGPD et légales sont intégrées.`,
        ],
      },
    ],
    faq: [
      { q: "Quels documents immobiliers sont disponibles ?", a: "Plus de 30 modèles : mandat, bon de visite, offre d'achat, compte-rendu, avis de valeur, argumentaire prix, checklists DPE et diagnostics, etc." },
      { q: "Les documents sont-ils conformes ?", a: "Oui, ils intègrent les mentions légales et RGPD et sont pensés pour la réglementation immobilière française." },
      { q: "Peut-on exporter en PDF ?", a: "Oui, chaque document s'exporte en PDF personnalisé à votre image professionnelle." },
    ],
    related: [
      { label: "Logiciel de gestion des mandats", to: "/logiciel-gestion-mandats" },
      { label: "Logiciel immobilier tout-en-un", to: "/logiciel-immobilier" },
      { label: "CRM immobilier", to: "/crm-immobilier" },
      { label: "Toutes les fonctionnalités", to: "/fonctionnalites" },
    ],
    cta: CTA_PARTNER,
  },

  // ================= HOME STAGING VIRTUEL =================
  "/home-staging-virtuel": {
    schemaType: "SoftwareApplication",
    badge: "Home staging IA",
    h1: "Home staging virtuel par intelligence artificielle",
    lead: `Transformez une pièce vide ou datée en coup de cœur. Le home staging virtuel ${APP} meuble, désencombre et modernise vos photos par IA, en préservant l'agencement réel du bien.`,
    intro: [
      `Une pièce vide se vend mal : l'acheteur peine à se projeter. Le home staging virtuel projette un aménagement réaliste sur vos photos, sans travaux ni mobilier physique, pour déclencher davantage de visites.`,
      `${APP} propose plusieurs styles et intensités, tout en respectant les volumes existants pour rester honnête vis-à-vis des acquéreurs.`,
    ],
    sections: [
      {
        h2: "Ce que permet le home staging IA",
        bullets: [
          "Meubler une pièce vide (furnish empty)",
          "Moderniser une pièce datée",
          "Désencombrer avant transformation",
          "Plusieurs styles (moderne, chaleureux…) et intensités",
          "Préservation de l'agencement et des volumes",
        ],
      },
      {
        h2: "Plus de visites, un cycle de vente raccourci",
        p: [
          `Des photos attractives augmentent le taux de clic sur les annonces et le nombre de visites. Associé aux annonces IA et à la retouche photo, le home staging virtuel accélère la commercialisation.`,
        ],
      },
    ],
    faq: [
      { q: "Qu'est-ce que le home staging virtuel ?", a: "C'est l'aménagement ou la modernisation d'une pièce directement sur la photo, par intelligence artificielle, sans mobilier physique ni travaux." },
      { q: "Est-ce trompeur pour l'acheteur ?", a: "Non, à condition de rester réaliste : ImmoProBoost préserve l'agencement et les volumes réels. Il est recommandé d'indiquer qu'il s'agit d'une projection." },
    ],
    related: [
      { label: "Retouche photo IA", to: "/logiciel-immobilier-ia" },
      { label: "Générateur d'annonce immobilière IA", to: "/generateur-annonce-immobiliere" },
      { label: "Agent immobilier IA", to: "/agent-immobilier-ia" },
      { label: "Logiciel immobilier tout-en-un", to: "/logiciel-immobilier" },
    ],
    cta: CTA_PARTNER,
  },

  // ================= VENDRE SON BIEN (grand public) =================
  "/vendre-son-bien-immobilier": {
    schemaType: "Article",
    badge: "Guide vendeur",
    h1: "Vendre son bien immobilier : étapes, prix et conseils",
    lead: `Vendre au meilleur prix demande méthode et bonnes informations : estimer juste, préparer les diagnostics, soigner l'annonce et négocier. Voici les étapes clés pour vendre son bien immobilier sereinement.`,
    intro: [
      `Vendre un bien immobilier est une décision importante. Un prix mal fixé fait fuir les acheteurs ou vous fait perdre de l'argent ; une annonce négligée réduit le nombre de visites. Ce guide vous aide à sécuriser chaque étape.`,
      `Pour aller plus loin, vous pouvez estimer gratuitement votre bien et vous appuyer sur un professionnel équipé de ${APP}.`,
    ],
    sections: [
      {
        h2: "Les étapes pour vendre son bien",
        bullets: [
          "Estimer le prix à partir des ventes réelles DVF du secteur",
          "Réaliser les diagnostics obligatoires (DPE, ERP…)",
          "Préparer des photos de qualité (home staging, retouche)",
          "Rédiger une annonce claire et conforme",
          "Organiser les visites et qualifier les acquéreurs",
          "Négocier et sécuriser l'offre d'achat",
        ],
      },
      {
        h2: "Bien fixer son prix de vente",
        p: [
          `Le prix se fonde sur des ventes comparables réelles, ajustées selon l'état, le DPE, l'étage et les travaux. Un prix réaliste attire plus de visites et se vend plus vite qu'un prix trop élevé qu'il faudra baisser.`,
        ],
      },
      {
        h2: "Faut-il passer par un professionnel ?",
        p: [
          `Un agent ou mandataire équipé de ${APP} vous fait gagner du temps, sécurise les diagnostics et la conformité, et défend votre prix avec des données. Vous pouvez démarrer par une estimation gratuite en ligne.`,
        ],
      },
    ],
    faq: [
      { q: "Comment estimer le prix de vente de mon bien ?", a: "En s'appuyant sur les ventes réelles DVF du secteur, ajustées selon l'état, le DPE, l'étage et les travaux. Vous pouvez obtenir une estimation gratuite en ligne sur ImmoProBoost." },
      { q: "Quels diagnostics sont obligatoires pour vendre ?", a: "Le DPE est obligatoire, ainsi que d'autres diagnostics selon le bien (amiante, plomb, termites, gaz, électricité, ERP). Une checklist est disponible dans ImmoProBoost." },
      { q: "Combien de temps pour vendre un bien ?", a: "Cela dépend du prix, du secteur et de la qualité de l'annonce. Un prix réaliste et des photos soignées raccourcissent significativement le délai de vente." },
    ],
    related: [
      { label: "Estimation immobilière gratuite", to: "/estimation-immobiliere-gratuite" },
      { label: "Prix au m²", to: "/prix-au-m2" },
      { label: "DPE et diagnostics", to: "/diagnostic-dpe" },
      { label: "Calcul net vendeur", to: "/calcul-net-vendeur" },
    ],
    cta: CTA_ESTIM,
  },

  // ================= DIAGNOSTIC DPE =================
  "/diagnostic-dpe": {
    schemaType: "Article",
    badge: "DPE & diagnostics",
    h1: "DPE et diagnostics immobiliers obligatoires",
    lead: `Le DPE (diagnostic de performance énergétique) classe un logement de A à G et doit figurer dans toute annonce. Voici ce que le DPE implique pour vendre ou louer, et les autres diagnostics obligatoires.`,
    intro: [
      `Le DPE est devenu central dans la décision d'achat et encadre la location des passoires thermiques. Comprendre le DPE et les diagnostics obligatoires évite les mauvaises surprises et sécurise la transaction.`,
      `${APP} intègre des checklists DPE et diagnostics, et affiche l'étiquette énergie/climat sur chaque annonce et page bien.`,
    ],
    sections: [
      {
        h2: "À quoi sert le DPE ?",
        p: [
          `Le DPE mesure la consommation d'énergie et les émissions de gaz à effet de serre d'un logement, avec deux étiquettes (énergie et climat) de A à G. Il informe l'acheteur ou le locataire et conditionne certaines obligations pour les logements les plus énergivores.`,
        ],
      },
      {
        h2: "Les diagnostics obligatoires courants",
        bullets: [
          "DPE (performance énergétique)",
          "ERP (état des risques et pollutions)",
          "Amiante, plomb (selon année de construction)",
          "Termites (selon zone)",
          "Gaz et électricité (selon ancienneté des installations)",
        ],
      },
      {
        h2: "DPE et affichage dans l'annonce",
        p: [
          `La classe énergie et la classe climat doivent apparaître dans les annonces immobilières. ${APP} affiche automatiquement l'étiquette DPE et injecte les mentions dans les annonces générées.`,
        ],
      },
    ],
    faq: [
      { q: "Le DPE est-il obligatoire pour vendre ?", a: "Oui, le DPE est obligatoire et doit figurer dans l'annonce, avec les classes énergie et climat. D'autres diagnostics peuvent s'appliquer selon le bien." },
      { q: "Qu'est-ce qu'une passoire thermique ?", a: "Un logement classé F ou G au DPE. Ces biens font l'objet d'obligations spécifiques, notamment pour la location." },
    ],
    related: [
      { label: "Vendre son bien immobilier", to: "/vendre-son-bien-immobilier" },
      { label: "Estimation immobilière gratuite", to: "/estimation-immobiliere-gratuite" },
      { label: "Logiciel immobilier tout-en-un", to: "/logiciel-immobilier" },
    ],
    cta: CTA_ESTIM,
  },
};

// Nouvelles définitions de pages (title/h1/desc) à fusionner dans seoDefinitions.
// Elles créent automatiquement la route ET doivent figurer dans le sitemap backend.
export const SEO_NEW_PAGES = {
  "/logiciel-immobilier": { title: "Logiciel immobilier tout-en-un pour agents & mandataires | ImmoProBoost", h1: "Logiciel immobilier tout-en-un", desc: "ImmoProBoost, le logiciel immobilier français en ligne : CRM, estimation DVF, annonces IA, mandats, documents et prospection. 21 modules, 99 €/mois." },
  "/meilleur-logiciel-immobilier": { title: "Meilleur logiciel immobilier 2026 : comparatif | ImmoProBoost", h1: "Meilleur logiciel immobilier", desc: "Comment choisir le meilleur logiciel immobilier ? Critères, comparatif et pourquoi ImmoProBoost réunit 21 modules pour agents et mandataires à 99 €/mois." },
  "/logiciel-immobilier-tout-en-un": { title: "Logiciel immobilier tout-en-un | ImmoProBoost", h1: "Logiciel immobilier tout-en-un", desc: "Un seul logiciel immobilier pour tout gérer : CRM, biens, annonces IA, estimation, documents et prospection. ImmoProBoost, 21 modules à 99 €/mois." },
  "/logiciel-agent-immobilier": { title: "Logiciel pour agent immobilier | ImmoProBoost", h1: "Logiciel pour agent immobilier", desc: "Le logiciel de l'agent immobilier moderne : CRM, estimation DVF, annonces IA, 30+ documents conformes et 35 calculateurs. Tout-en-un à 99 €/mois." },
  "/logiciel-de-prospection-immobiliere": { title: "Logiciel de prospection immobilière | ImmoProBoost", h1: "Logiciel de prospection immobilière", desc: "Jusqu'à 25 prospects géolocalisés par jour, pige, courriers IA et boîtage. Le logiciel de prospection immobilière ImmoProBoost pour capter des mandats." },
  "/logiciel-pige-immobiliere": { title: "Logiciel de pige immobilière | ImmoProBoost", h1: "Logiciel de pige immobilière", desc: "Automatisez votre pige immobilière : particuliers (Leboncoin, PAP), ventes récentes, scripts d'appel et relances. Inclus dans ImmoProBoost." },
  "/logiciel-estimation-immobiliere": { title: "Logiciel d'estimation immobilière (DVF) | ImmoProBoost", h1: "Logiciel d'estimation immobilière", desc: "Estimation appuyée sur les ventes réelles DVF, ajustée par l'état, le DPE et les travaux, avec rapport d'expertise PDF. Le logiciel d'estimation ImmoProBoost." },
  "/logiciel-gestion-mandats": { title: "Logiciel de gestion des mandats immobiliers | ImmoProBoost", h1: "Logiciel de gestion des mandats", desc: "Numérisez (OCR), suivez et pilotez vos mandats : fiche bien automatique, plan de commercialisation J+7 à J+60 et compte-rendus vendeur." },
  "/logiciel-negociateur-immobilier": { title: "Logiciel pour négociateur immobilier | ImmoProBoost", h1: "Logiciel pour négociateur immobilier", desc: "Les outils d'une grande agence pour le négociateur : CRM, estimation DVF, annonces IA, documents de négociation et prospection. 99 €/mois." },
  "/prospection-immobiliere": { title: "Prospection immobilière : méthodes et outils 2026 | ImmoProBoost", h1: "Prospection immobilière", desc: "Pige, boîtage, phoning, DPE récents : les méthodes de prospection immobilière qui marchent et comment les automatiser avec ImmoProBoost." },
};

export default SEO_CONTENT;
